<template>
  <div class="cat-grupo-material">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-grupo-material-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${grupoMateriales.length} Grupos`"
          @new="addGrupoMaterial"
          @refresh="loadGrupoMateriales"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando Grupos de Materiales...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!grupoMateriales || grupoMateriales.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay Grupos de Materiales registrados. Haga clic en 'Nuevo' para agregar uno.
          </div>
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && grupoMateriales && grupoMateriales.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="grupoMateriales"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'Clave', dir: 'asc' }"
            filter-placeholder="Buscar Grupos de Materiales..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editGrupoMaterial(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar Grupo de Material"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.Clave)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar Grupo de Material"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Grupo de Material -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="grupoMaterialModal" 
      tabindex="-1" 
      aria-labelledby="grupoMaterialModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="grupoMaterialModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Grupo de Material' : 'Nuevo Grupo de Material' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatGrupoMaterialForm
              :grupoMaterial="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatGrupoMaterialService from '../controllers/CatGrupoMaterialController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatGrupoMaterialForm from '../components/CatGrupoMaterialForm.vue';

export default {
  name: 'CatGrupoMaterialView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatGrupoMaterialForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: new CatGrupoMaterialService(),
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        Clave: '',
        EmpresaId: null,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        CuentaContable: ''
      }
    };
  },
  computed: {
    grupoMateriales() {
      return this.service.grupoMateriales;
    },
    loading() {
      return this.service.loading;
    },
    error() {
      return this.service.error;
    },
    hasEmpresa() {
      const emp = JSON.parse(localStorage.getItem('empresa_seleccionada') || 'null');
      return !!(emp && emp.EmpresaId);
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'Clave', label: 'Clave', sortable: true },
        { key: 'EmpresaId', label: 'Empresa ID', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
        { key: 'EsActivo', label: 'Activo', sortable: true },
        { key: 'CuentaContable', label: 'Cuenta Contable', sortable: true }
      ];
    }
  },
  created() {
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadGrupoMateriales() {
      console.log('Cargando Grupos de Materiales...');
      await this.service.listar();
    },
    addGrupoMaterial() {
      this.isEditing = false;
      this.openModal();
    },
    async editGrupoMaterial(grupoMaterial) {
      this.isEditing = true;
      try {
        // Cargar datos completos del Grupo de Material
        const response = await this.service.lectura(grupoMaterial.Clave);
        if (response && response.data) {
          this.formData = { ...response.data };
        } else {
          this.formData = { ...grupoMaterial };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar Grupo de Material:', error);
        this.$alert.error('No se pudo cargar el Grupo de Material para edición.');
      }
    },
    confirmDelete(clave) {
      console.log('Iniciando confirmación de eliminación para:', clave);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el Grupo de Material "${clave}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteGrupoMaterial(clave);
        }
      });
    },
    async deleteGrupoMaterial(clave) {
      console.log('Ejecutando eliminación para:', clave);
      
      try {
        const response = await this.service.borrar({ Clave: clave });
        console.log('Respuesta de eliminación recibida:', response);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Grupo de Material eliminado exitosamente');
          // Recargar datos
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo eliminar el Grupo de Material. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al eliminar Grupo de Material:', error);
        this.$alert.error('No se pudo eliminar el Grupo de Material. Por favor, intente nuevamente.');
      }
    },
    async downloadBulk() {
      try {
        await this.service.downloadBulk();
        this.$alert.success('Descarga iniciada correctamente');
      } catch (error) {
        this.$alert.error('Error al descargar: ' + error.message);
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Clave: '',
        EmpresaId: null,
        Nombre: '',
        ClaveErp: '',
        EsActivo: true,
        CuentaContable: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        const response = await this.service.guardar(formData);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Grupo de Material guardado exitosamente');
          // Cerrar modal y recargar datos
          this.closeModal();
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo guardar el Grupo de Material. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al guardar Grupo de Material:', error);
        this.$alert.error('No se pudo guardar el Grupo de Material. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.cat-grupo-material {
  min-height: calc(100vh - 200px);
}

.cat-grupo-material-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>
